import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Send, Ban, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const COMPANY_LABEL = { sugarhathi: 'SugarHathi', krishilinks: 'KrishiLinks' };
const DOC_TYPE_LABEL = { rate_request_letter: 'Rate Request Letter', purchase_order: 'Purchase Order', proforma_invoice: 'Proforma Invoice' };

const CLAWBACK_REASONS = [
  'Clerical/typographical error',
  'Market price volatility',
  'Sent in error/duplicate',
  'Upstream supplier non-performance',
  'Quantity no longer available',
  'Other (specify)',
];

const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const style = status === 'withdrawn'
    ? { bg: '#fee2e2', color: '#DC143C' }
    : { bg: '#dcfce7', color: '#16a34a' };
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.color }}>
      {status === 'withdrawn' ? 'Withdrawn' : 'Sent'}
    </span>
  );
};

const EmailsTab = ({ isAdminView }) => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [senderNames, setSenderNames] = useState({});
  const [expandedId, setExpandedId] = useState(null); // resend panel open for this row
  const [withdrawId, setWithdrawId] = useState(null); // clawback panel open for this row
  const [editForm, setEditForm] = useState({});
  const [withdrawReason, setWithdrawReason] = useState(CLAWBACK_REASONS[0]);
  const [withdrawOther, setWithdrawOther] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase
        .from('fro_sent_emails')
        .select('*')
        .order('last_sent_at', { ascending: false });
      if (error) throw error;
      setRows(data || []);

      if (isAdminView && data?.length) {
        const ids = [...new Set(data.map(r => r?.sent_by).filter(Boolean))];
        const { data: profiles } = await supabase.from('user_profiles').select('user_id, full_name').in('user_id', ids);
        const map = {};
        (profiles || []).forEach(p => { map[p.user_id] = p.full_name; });
        setSenderNames(map);
      }
    } catch (e) {
      setFetchError(e?.message || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  }, [isAdminView]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const canAct = (row) => row?.sent_by === user?.id || isAdminView;

  const openResend = (row) => {
    setExpandedId(row?.id);
    setWithdrawId(null);
    setActionMsg(null);
    if (row?.email_type === 'purchase_order') {
      setEditForm({
        supplier_email: row?.recipient_email || '',
        supplier_name: row?.recipient_name || '',
        supplier_address: row?.recipient_address || '',
        delivery_terms: row?.document_data?.delivery_terms || '',
        payment_terms: row?.document_data?.payment_terms || '',
        share_contact_number: row?.share_contact_number || false,
        contact_number: row?.contact_number || '',
      });
    } else if (row?.email_type === 'proforma_invoice') {
      setEditForm({
        buyer_email: row?.recipient_email || '',
        buyer_name: row?.recipient_name || '',
        buyer_address: row?.recipient_address || '',
        valid_till: row?.document_data?.valid_till || '',
        share_contact_number: row?.share_contact_number || false,
        contact_number: row?.contact_number || '',
      });
    } else {
      setEditForm({
        recipient_email: row?.recipient_email || '',
        recipient_name: row?.recipient_name || '',
        recipient_address: row?.recipient_address || '',
        commodity_name: row?.commodity_name || '',
        quantity_mt: row?.document_data?.quantity_mt || '',
        grade: row?.grade || '',
        share_contact_number: row?.share_contact_number || false,
        contact_number: row?.contact_number || '',
      });
    }
  };

  const confirmResend = async (id) => {
    setActionBusy(true);
    setActionMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-fro-email', {
        body: { action: 'resend', id, ...editForm },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setActionMsg({ id, success: true, text: 'Resent successfully — date updated.' });
      setExpandedId(null);
      loadRows();
    } catch (e) {
      setActionMsg({ id, success: false, text: e?.message || 'Resend failed' });
    } finally {
      setActionBusy(false);
    }
  };

  const openWithdraw = (row) => {
    setWithdrawId(row?.id);
    setExpandedId(null);
    setActionMsg(null);
    setWithdrawReason(CLAWBACK_REASONS[0]);
    setWithdrawOther('');
  };

  const confirmWithdraw = async (id) => {
    const reason = withdrawReason === 'Other (specify)' ? withdrawOther?.trim() : withdrawReason;
    if (!reason) return;
    setActionBusy(true);
    setActionMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-fro-email', {
        body: { action: 'withdraw', id, reason },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setActionMsg({ id, success: true, text: 'Clawback sent — recipient notified.' });
      setWithdrawId(null);
      loadRows();
    } catch (e) {
      setActionMsg({ id, success: false, text: e?.message || 'Clawback failed' });
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-sm" style={{ color: '#9ca3af' }}>Loading emails...</div>;
  }
  if (fetchError) {
    return <div className="rounded-lg p-4 text-sm" style={{ background: '#fee2e2', color: '#DC143C' }}>{fetchError}</div>;
  }
  if (rows?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-base font-semibold" style={{ color: '#1f2937' }}>No emails sent yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows?.map((row) => (
        <div key={row?.id} className="bg-white rounded-xl px-4 py-3" style={{ border: '1px solid #f3e8d0' }}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={row?.status} />
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                {COMPANY_LABEL?.[row?.sending_company] || row?.sending_company}
              </span>
              <span className="text-sm font-bold" style={{ color: '#1f2937' }}>{row?.recipient_name}</span>
              {row?.email_type === 'purchase_order' ? (
                <span className="text-xs" style={{ color: '#6b7280' }}>{row?.document_data?.po_number}</span>
              ) : row?.email_type === 'proforma_invoice' ? (
                <span className="text-xs" style={{ color: '#6b7280' }}>{row?.document_data?.invoice_number}</span>
              ) : (
                <span className="text-xs" style={{ color: '#6b7280' }}>{row?.commodity_name}{row?.grade ? ` · ${row?.grade}` : ''}</span>
              )}
            </div>
            <span className="text-xs flex-shrink-0" style={{ color: '#9ca3af' }}>
              {formatDate(row?.last_sent_at)}
              {row?.resend_count > 0 ? ` (resent ${row?.resend_count}×)` : ''}
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs" style={{ color: '#9ca3af' }}>
              {DOC_TYPE_LABEL?.[row?.email_type] || row?.email_type} · to {row?.recipient_email}
              {isAdminView && senderNames?.[row?.sent_by] && ` · by ${senderNames[row.sent_by]}`}
            </span>
            {row?.pdf_url && (
              <a href={row?.pdf_url} target="_blank" rel="noreferrer" className="text-xs font-semibold underline" style={{ color: '#1e4a72' }}>
                View PDF
              </a>
            )}
          </div>

          {row?.status === 'withdrawn' && row?.withdrawn_reason && (
            <div className="mt-2 text-xs rounded-lg px-3 py-2" style={{ background: '#fef2f2', color: '#991b1b' }}>
              Withdrawn on {formatDate(row?.withdrawn_at)} — Reason: {row?.withdrawn_reason}
            </div>
          )}

          {actionMsg?.id === row?.id && (
            <div className="mt-2 text-xs rounded-lg px-3 py-2" style={actionMsg?.success ? { background: '#dcfce7', color: '#16a34a' } : { background: '#fee2e2', color: '#DC143C' }}>
              {actionMsg?.text}
            </div>
          )}

          {row?.status === 'sent' && canAct(row) && (
            <div className="flex gap-2 mt-2.5">
              <button
                onClick={() => expandedId === row?.id ? setExpandedId(null) : openResend(row)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border"
                style={{ borderColor: '#1e4a72', color: '#1e4a72' }}
              >
                <RefreshCw size={12} /> Resend
                {expandedId === row?.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              <button
                onClick={() => withdrawId === row?.id ? setWithdrawId(null) : openWithdraw(row)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border"
                style={{ borderColor: '#DC143C', color: '#DC143C' }}
              >
                <Ban size={12} /> Clawback
                {withdrawId === row?.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            </div>
          )}

          {expandedId === row?.id && (
            <div className="mt-3 rounded-lg p-3 space-y-2" style={{ background: '#f9fafb' }}>
              {row?.email_type === 'purchase_order' ? (
                <>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>Item lines can't be edited here — to change items, send a new PO instead.</p>
                  <input
                    value={editForm?.supplier_name}
                    onChange={(e) => setEditForm(f => ({ ...f, supplier_name: e.target.value }))}
                    placeholder="Supplier Name"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <input
                    value={editForm?.supplier_email}
                    onChange={(e) => setEditForm(f => ({ ...f, supplier_email: e.target.value }))}
                    placeholder="Supplier Email"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <textarea
                    value={editForm?.delivery_terms}
                    onChange={(e) => setEditForm(f => ({ ...f, delivery_terms: e.target.value }))}
                    placeholder="Delivery Terms"
                    rows={2}
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                </>
              ) : row?.email_type === 'proforma_invoice' ? (
                <>
                  <p className="text-xs" style={{ color: '#9ca3af' }}>Item lines can't be edited here — to change items, send a new invoice instead.</p>
                  <input
                    value={editForm?.buyer_name}
                    onChange={(e) => setEditForm(f => ({ ...f, buyer_name: e.target.value }))}
                    placeholder="Buyer Name"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <input
                    value={editForm?.buyer_email}
                    onChange={(e) => setEditForm(f => ({ ...f, buyer_email: e.target.value }))}
                    placeholder="Buyer Email"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <input
                    type="date"
                    value={editForm?.valid_till}
                    onChange={(e) => setEditForm(f => ({ ...f, valid_till: e.target.value }))}
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                </>
              ) : (
                <>
                  <input
                    value={editForm?.recipient_name}
                    onChange={(e) => setEditForm(f => ({ ...f, recipient_name: e.target.value }))}
                    placeholder="Recipient Company Name"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <input
                    value={editForm?.recipient_email}
                    onChange={(e) => setEditForm(f => ({ ...f, recipient_email: e.target.value }))}
                    placeholder="Recipient Email"
                    className="w-full rounded-lg border px-2.5 py-2 text-xs"
                    style={{ borderColor: '#e5e7eb' }}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={editForm?.commodity_name}
                      onChange={(e) => setEditForm(f => ({ ...f, commodity_name: e.target.value }))}
                      placeholder="Commodity"
                      className="rounded-lg border px-2.5 py-2 text-xs"
                      style={{ borderColor: '#e5e7eb' }}
                    />
                    <input
                      type="number"
                      value={editForm?.quantity_mt}
                      onChange={(e) => setEditForm(f => ({ ...f, quantity_mt: e.target.value }))}
                      placeholder="Quantity (MT)"
                      className="rounded-lg border px-2.5 py-2 text-xs"
                      style={{ borderColor: '#e5e7eb' }}
                    />
                    <input
                      value={editForm?.grade}
                      onChange={(e) => setEditForm(f => ({ ...f, grade: e.target.value }))}
                      placeholder="Grade"
                      className="rounded-lg border px-2.5 py-2 text-xs"
                      style={{ borderColor: '#e5e7eb' }}
                    />
                  </div>
                </>
              )}
              <button
                onClick={() => confirmResend(row?.id)}
                disabled={actionBusy}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white"
                style={{ background: actionBusy ? '#9ca3af' : '#1e4a72' }}
              >
                <Send size={12} /> {actionBusy ? 'Sending...' : 'Confirm Resend'}
              </button>
            </div>
          )}

          {withdrawId === row?.id && (
            <div className="mt-3 rounded-lg p-3 space-y-2" style={{ background: '#fef2f2' }}>
              <select
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                className="w-full rounded-lg border px-2.5 py-2 text-xs"
                style={{ borderColor: '#fecaca' }}
              >
                {CLAWBACK_REASONS?.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {withdrawReason === 'Other (specify)' && (
                <input
                  value={withdrawOther}
                  onChange={(e) => setWithdrawOther(e.target.value)}
                  placeholder="Specify reason"
                  className="w-full rounded-lg border px-2.5 py-2 text-xs"
                  style={{ borderColor: '#fecaca' }}
                />
              )}
              <button
                onClick={() => confirmWithdraw(row?.id)}
                disabled={actionBusy || (withdrawReason === 'Other (specify)' && !withdrawOther?.trim())}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white"
                style={{ background: actionBusy ? '#9ca3af' : '#DC143C' }}
              >
                <Ban size={12} /> {actionBusy ? 'Sending...' : 'Confirm Clawback'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EmailsTab;
